import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Auth0WrapperService, getCookie } from '@auth/bp-pf-auth';
import { environment } from '../../environments/environment';


const DIRECTIVE_PATTERN = /[?&](email_id|connection_id)\s*[:=]\s*([^&]*)/gi;

function cleanDirectiveValue(rawValue: string): string {
  let value = rawValue;
  try {
    value = decodeURIComponent(value);
  } catch {
    // value wasn't URI-encoded; use as-is
  }
  return value.trim().replace(/^["']+/, '').replace(/["']+$/, '').trim();
}


function getDirectivesFromUrl(url: string): { emailId: string; connectionId: string } {
  const result = { emailId: '', connectionId: '' };
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return result;
  }

  const query = url.slice(queryIndex);
  DIRECTIVE_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = DIRECTIVE_PATTERN.exec(query))) {
    const value = cleanDirectiveValue(match[2] ?? '');
    if (match[1].toLowerCase() === 'email_id') {
      result.emailId = value;
    } else {
      result.connectionId = value;
    }
  }
  return result;
}


function stripQuery(url: string): string {
  return url.split('?')[0].replace(/^\/+/, '');
}

function stripDirectiveQueryParams(url: string): string {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return url;
  }

  const basePath = url.slice(0, queryIndex);
  const remaining = url
    .slice(queryIndex + 1)
    .split('&')
    .filter(pair => !/^(email_id|connection_id)\s*[:=]/i.test(pair))
    .join('&');

  return remaining ? `${basePath}?${remaining}` : basePath;
}

/** Decodes a JWT's payload segment (base64url) without verifying the signature. */
function decodeTokenPayload(token: string): Record<string, any> | null {
  try {
    const base64 = token.split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padLength = (4 - (base64.length % 4)) % 4;
    const paddedBase64 = base64.padEnd(base64.length + padLength, '=');
    return JSON.parse(atob(paddedBase64));
  } catch {
    return null;
  }
}


function enforceEmailIdentity(tenant: string, router: Router, state: RouterStateSnapshot): boolean {
  const { emailId, connectionId } = getDirectivesFromUrl(state.url);
  if ((!emailId && !connectionId) || !tenant) {
    return true;
  }

  const token = getCookie(`${tenant}_${environment.app_id}_access_token`);
  if (!token) {
    return true;
  }

  const cookieEmail = decodeURIComponent(getCookie(`${tenant}_${environment.app_id}_email`) || '');
  const emailMatches = !emailId || (!!cookieEmail && cookieEmail.toLowerCase() === emailId.toLowerCase());

  const claims = decodeTokenPayload(token);
  const tenantData = claims?.['https://pf_bp/tenant_data'] ?? {};
  const tokenConnectionId = String(tenantData['connection_id'] ?? '');
  const connectionMatches = !connectionId || (!!tokenConnectionId && tokenConnectionId.toLowerCase() === connectionId.toLowerCase());

  if (emailMatches && connectionMatches) {
    return true;
  }

  inject(Auth0WrapperService).clearAuthStorage(environment.app_id, tenant);
  router.navigateByUrl(state.url, { onSameUrlNavigation: 'reload' });
  return false;
}

function redirectToBasicDetails(
  tenant: string,
  submissionId: string,
  quoteIdFromUrl: string,
  router: Router
): boolean {
  if (quoteIdFromUrl) {
    sessionStorage.setItem('quoteId', quoteIdFromUrl);
  } else {
    sessionStorage.removeItem('quoteId');
  }

  sessionStorage.setItem('isIndicationDeepLink', 'true');

  router.navigate(['/', tenant, 'quickquote', submissionId, 'edit']);
  return false;
}
function findLeafRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  return route.firstChild ? findLeafRoute(route.firstChild) : route;
}

export const authGuard: CanActivateFn = (route, state: RouterStateSnapshot) => {
  const router = inject(Router);

  const pathSegments = state.url.split('?')[0].split('/');
  const tenant = pathSegments[1] || getCookie(`${environment.app_id}_tenant_code`) || '';

  if (!enforceEmailIdentity(tenant, router, state)) {
    return false;
  }

  const token = getCookie(`${tenant}_${environment.app_id}_access_token`);
  const wasLoggedIn = localStorage.getItem(`${tenant}_${environment.app_id}_wasLoggedIn`) === 'true';


   if (!token) {
    if (tenant) {
      localStorage.setItem(`${tenant}_${environment.app_id}_returnUrl`, stripDirectiveQueryParams(state.url));
    }

    const { emailId, connectionId } = getDirectivesFromUrl(state.url);
    const queryParams: Record<string, string> = {
      returnUrl: stripDirectiveQueryParams(state.url)
    };
    if (emailId) {
      queryParams['email_id'] = emailId;
    }
    if (connectionId) {
      queryParams['connection_id'] = connectionId;
    }

    if (wasLoggedIn) {
      router.navigate(['/', tenant, 'logout'], { queryParams });
    } else {
      router.navigate(['/', tenant], { queryParams });
    }
    return false;
  }

  const returnUrlKey = `${tenant}_${environment.app_id}_returnUrl`;
  const isDeepLinkReturn = localStorage.getItem(returnUrlKey) === stripDirectiveQueryParams(state.url);
  if (isDeepLinkReturn) {
    localStorage.removeItem(returnUrlKey);
  }

  const leaf = findLeafRoute(route);
  const matchedPath = leaf.routeConfig?.path;
  const isSubmissionDeepLink = matchedPath === 'submission/:id' || matchedPath === 'submission/:id/:quoteId';

  const { emailId, connectionId } = getDirectivesFromUrl(state.url);

  if (isSubmissionDeepLink && (isDeepLinkReturn || emailId || connectionId)) {
    const submissionId = leaf.paramMap.get('id') ?? '';
    const quoteIdFromUrl = leaf.paramMap.get('quoteId') ?? '';
    return redirectToBasicDetails(tenant, submissionId, quoteIdFromUrl, router);
  }

  return true;
};